import React, { useCallback, useRef } from 'react'

import { styles, useStyles } from '@signavio/ui'
import { useOnClickOutside } from 'usehooks-ts'

import { useMediaQuery } from '../../hooks/useMedia'

export const DEFAULT_MIN_WIDTH_PANEL = '250px'
export const DEFAULT_COLLAPSED_WIDTH_PANEL = '56px'

type SidePanelProps = {
  children: React.ReactNode
  isOpen?: boolean
  width?: number
  collapseWidth?: number
  onRequestCollapse?: () => void
  panel: React.ReactNode
}

export const SidePanel = ({
  children,
  panel,
  isOpen = false,
  onRequestCollapse,
}: SidePanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null)

  const isMdOrSmaller = useMediaQuery({ maxWidth: styles.media.lgMin })
  const showOverlayEffect = isOpen && isMdOrSmaller

  const style = useStyles(
    defaultStyles,
    {},
    {
      '&expanded': showOverlayEffect,
      '&isOpen': isOpen,
    }
  )

  const handleClickOutside = useCallback(() => {
    if (isMdOrSmaller) {
      onRequestCollapse?.()
    }
  }, [isMdOrSmaller, onRequestCollapse])

  useOnClickOutside(panelRef, handleClickOutside)

  return (
    <div {...style('mainContainer')}>
      {panel && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <aside {...style('panelWrapper')} data-testid="panel-wrapper">
          <div {...style('panel')} ref={panelRef}>
            {panel}
          </div>
        </aside>
      )}
      <section {...style('mainContent')}>{children}</section>
    </div>
  )
}

const defaultStyles = () => {
  const height = `calc(100vh - ${DEFAULT_COLLAPSED_WIDTH_PANEL})`

  return {
    mainContainer: {
      display: 'flex',
    },

    mainContent: {
      flex: '1',
      overflowX: 'hidden',
      position: 'relative',
    },

    panel: {
      backgroundColor: 'var(--color-surface)',
      height,
      width: DEFAULT_COLLAPSED_WIDTH_PANEL,
    },

    '&useBackgroundColor': {
      panel: {
        background: 'var(--color-background)',
      },
    },

    '&isOpen': {
      panel: {
        width: DEFAULT_MIN_WIDTH_PANEL,
      },
    },

    '&noBackground': {
      panel: {
        background: 'none',
      },
    },

    panelWrapper: {
      borderRight: '1px solid rgba(0, 0, 0, 0.16)',
    },

    '&expanded': {
      panelWrapper: {
        left: '0',
        position: 'absolute',
        height,
        width: '100%',
        zIndex: '999',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
      },
    },
  }
}
